/*
	Contrato real de GET promotions/_applications y /{id} (PromocionAdelantoService::listar / detalle, docs/REPORTE_PROMOS_ADELANTO_A.md).
*/
export interface AdvisorShare { userId?: number | null; name: string; role?: string | null; sharePercentage?: number | null }

export interface PromoApplicationRow {
	id: number;
	statusId: number;
	status?: string | null;
	kind: "meses" | "kit" | "dos_x_uno" | "todos_juntos" | string;
	kindName?: string | null;
	promotionId: number;
	promoName: string;
	student: { id: number; name: string; identityCode?: string | null };
	enrollmentId: number;
	courseName?: string | null;
	scheduleText?: string | null;
	giftStudent?: { id: number; name: string } | null;
	giftEnrollmentId?: number | null;
	giftCourseName?: string | null;
	giftOwedArticleId?: number | null;
	agreementId?: number | null;
	/** null cuando se aplicó sin cobrar (origin = sin_cobro): no hay recibo. */
	receiptId: number | null;
	paymentDate: string | null;
	amountPaid: number;
	giftValue: number;
	origin?: string | null;
	requestId?: number | null;
	advisors?: AdvisorShare[];
	cashier?: { id: number; name: string } | null;
	createdAt?: string | null;
	cancelledAt?: string | null;
	cancelReason?: string | null;
	/** Quién anuló. */
	cancelledBy?: { id: number; name: string } | null;
	/** Promos que esta reemplazó, ya armado: «A» y «B». */
	replacedPromos?: string | null;
	/** Kit regalado y su entrega (pestaña Kits por Entregar). Compat: el 1er producto; lo nuevo viene en `gifts`. */
	giftKit?: { name: string; delivery?: string | null; deliveryStatusId?: number; cancelled?: boolean } | null;
	/** Todos los productos que regaló (docs/ESPEC_PROMO_REGALOS.md), cada uno con su cargo (tb_owed_articles) y su entrega. */
	gifts?: GiftRow[] | null;
	/** La alternativa de regalo que eligió Caja. */
	giftOptionLabel?: string | null;
	/** Quién la pidió (Solicitud de Promoción de la asesora, ESPEC_SOLICITUD_PROMO); `cashier` = quién la aplicó. */
	requestedBy?: { id: number; name: string } | null;
}

/** Un producto regalado. `deliveryStatus`: texto («Sin entregar», «Entregado»…) o el número de OwedArticle::DELIVERY_*. */
export interface GiftRow {
	owedArticleId?: number | null;
	articleId?: number | null;
	name: string;
	quantity?: number | null;
	value?: number | null;
	deliveryStatus?: string | number | null;
	delivery?: string | null;
	deliveredAt?: string | null;
	cancelled?: boolean;
}

/** Pestañas del módulo (lo manda y cuenta el servidor con el alcance de quien mira). */
/** `enCamino` y `fallidas` salen de promotions/_enCamino (otra lista); las demás de promotions/_applications. */
export type TabKey = "porAplicar" | "enCamino" | "aplicadas" | "kits" | "anuladas" | "fallidas" | ExtraTabKey;
/**
 * Lo de cada alumna que vivía en Descuentos (Módulos Claros, 2026-11-01): Acuerdos por Matrícula siempre; Por Resolver y Por Aprobar
 * solo cuando hay algo; Limpieza para Caja mientras queden matrículas por revisar.
 */
export type ExtraTabKey = "acuerdos" | "porResolver" | "porAprobar" | "limpieza";
export const EXTRA_TABS: ExtraTabKey[] = ["acuerdos", "porResolver", "porAprobar", "limpieza"];
export const TABS_EN_CAMINO: TabKey[] = ["enCamino", "fallidas"];
export type TabCounts = Partial<Record<TabKey, number>>;
export const TABS: { key: TabKey; label: string }[] = [
	{ key: "porAplicar", label: "Por Aplicar" },
	{ key: "enCamino", label: "En Camino" },
	{ key: "aplicadas", label: "Aplicadas" },
	{ key: "kits", label: "Kits por Entregar" },
	// Fallidas vive dentro de Anuladas (Anuladas · Fallidas), como Rechazadas en Matrículas (2026-11-01).
	{ key: "anuladas", label: "Anuladas" },
];

export interface PendingPerson { studentId: number | null; name: string; identityCode?: string | null; enrollmentId?: number | null; courseName?: string | null; scheduleText?: string | null }

/** Una fila de «Por Aplicar»: ticket, solicitud en revisión o acuerdo viejo; un 2x1 trae a quien paga y a quien recibe juntos. */
export interface PendingRow {
	key: string;
	source: "ticket" | "solicitud" | "acuerdo" | string;
	sourceName?: string;
	kind?: string | null;
	kindName?: string | null;
	promotionId: number;
	promoName: string;
	payer: PendingPerson;
	receiver?: PendingPerson | null;
	ticketId?: number | null;
	requestId?: number | null;
	agreementId?: number | null;
	amount?: number | null;
	/** A quién va cuando no hay ticket («Caja», «Admisiones Ana»). */
	goesTo?: string | null;
	createdAt?: string | null;
	advisors?: AdvisorShare[];
	/** Ticket nacido de una Solicitud de Promoción (`source: "solicitudPromo"`): quién la pidió. */
	requestedBy?: { id: number; name: string } | null;
}
export const SOURCE_SOLICITUD_PROMO = "solicitudPromo";

export interface PromoApplicationFiltersData {
	promotions: { id: number; name: string }[];
	advisors: { id: number; name: string }[];
	statuses: { id: number; name: string }[];
	kinds: { id: string; name: string }[];
}

export interface PromoApplicationList<T = PromoApplicationRow> {
	data: T[];
	counts?: TabCounts;
	tab?: TabKey;
	total: number;
	page: number;
	perPage: number;
	currencySymbol?: string;
	/** Solo lo Aplicado, con los filtros puestos. */
	totals?: { aplicadas: number; cobrado: number; regalado: number };
	filters?: PromoApplicationFiltersData;
	/** Quien mira puede crear Solicitudes de Promoción (botón «Nueva Promoción»). */
	canCreate?: boolean;
}

/** Un mes de la tira (MotorAcuerdos::tiraDeMeses): `state` ya viene en el idioma de TiraDeMeses. */
export interface PromoApplicationMonth { tuitionId: number; n: number; title?: string; state: string; listAmount?: number | null; subTotal?: number | null; paidAmount?: number | null }
export interface MonthStrip { agreements?: any[]; months: PromoApplicationMonth[] }

export interface PromoApplicationDetail extends PromoApplicationRow {
	currencySymbol?: string;
	months?: MonthStrip | null;
	giftMonths?: MonthStrip | null;
	agreement?: { id: number; statusId: number; status?: string | null; enrollmentId: number } | null;
	kit?: { id: number; title?: string | null; name: string; value?: number | null; statusId?: number; deliveryStatusId?: number; delivery?: string | null; deliveredAt?: string | null; cancelled?: boolean; invoiceId?: number | null } | null;
	receipt?: { id: number; total: number; paymentDate: string | null; reference?: string | null; method?: string | null; cancelled?: boolean; cancelNotes?: string | null } | null;
	history?: { id: number; reason: string; reasonName?: string | null; text: string; at: string; statusId?: number }[];
	/** Eventos del acuerdo (cumplido, regalo saldado, anulado…), si el servidor los manda. */
	agreementEvents?: { id: number; name?: string | null; text?: string | null; at?: string | null; by?: string | null }[];
}

export const ORIGIN_LABEL: Record<string, string> = { cobros: "Cobros", ticket: "Ticket de Pago", matricula: "Solicitud de Matrícula", sin_cobro: "Sin Cobro", solicitudPromo: "Solicitud de Promoción" };
export const ORIGIN_SIN_COBRO = "sin_cobro";
export const STATUS_APLICADA = 1;
export const STATUS_ANULADA = 2;
